/**
 * Master Bot - Analytics Page
 * Revenue charts and bot performance metrics
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Navbar from '../../components/Navbar';
import StatsCard from '../../components/StatsCard';
import Chart from '../../components/Chart';
import Card from '../../components/Card';
import { useTelegram } from '../../hooks/useTelegram';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
`;

const PeriodSelector = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-lg);
  background: var(--bg-card);
  padding: var(--spacing-sm);
  border-radius: var(--radius-md);
`;

const PeriodButton = styled.button<{ active: boolean }>`
  flex: 1;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--radius-md);
  font-size: var(--font-sm);
  font-weight: var(--font-medium);
  background: ${({ active }) => (active ? 'var(--accent-green)' : 'transparent')};
  color: ${({ active }) => (active ? 'white' : 'var(--text-secondary)')};
  transition: all var(--transition);
`;

const BotPerformance = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);
`;

const BotCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const BotHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BotName = styled.h4`
  font-size: var(--font-lg);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
`;

const BotMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-radius: var(--radius-md);
`;

const Metric = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs);
`;

const MetricValue = styled.div`
  font-size: var(--font-lg);
  font-weight: var(--font-bold);
  color: var(--text-primary);
`;

const MetricLabel = styled.div`
  font-size: var(--font-xs);
  color: var(--text-secondary);
  text-align: center;
`;

const SectionTitle = styled.h3`
  font-size: var(--font-lg);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
  margin: var(--spacing-lg) 0 var(--spacing-md);
`;

const MasterAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const { showBackButton } = useTelegram();
  const [period, setPeriod] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [bots] = useState([
    {
      id: 1,
      name: 'My Store Bot',
      users: 150,
      orders: 45,
      revenue: 1234.0,
      growth: 12,
    },
    {
      id: 2,
      name: 'Shop Assistant',
      users: 23,
      orders: 5,
      revenue: 156.0,
      growth: -5,
    },
  ]);

  useEffect(() => {
    showBackButton(() => navigate('/master/dashboard'));
  }, [showBackButton, navigate]);

  const totalRevenue = bots.reduce((sum, bot) => sum + bot.revenue, 0);
  const totalUsers = bots.reduce((sum, bot) => sum + bot.users, 0);
  const totalOrders = bots.reduce((sum, bot) => sum + bot.orders, 0);

  return (
    <Layout header={<Navbar title="Analytics" subtitle="Performance Overview" showBack />}>
      <PeriodSelector>
        <PeriodButton active={period === '7d'} onClick={() => setPeriod('7d')}>
          7 Days
        </PeriodButton>
        <PeriodButton active={period === '30d'} onClick={() => setPeriod('30d')}>
          30 Days
        </PeriodButton>
        <PeriodButton active={period === '90d'} onClick={() => setPeriod('90d')}>
          90 Days
        </PeriodButton>
        <PeriodButton active={period === '1y'} onClick={() => setPeriod('1y')}>
          1 Year
        </PeriodButton>
      </PeriodSelector>

      <StatsGrid>
        <StatsCard
          label="Total Revenue"
          value={`$${totalRevenue.toFixed(2)}`}
          icon="💰"
          color="var(--accent-green)"
          change={{ value: '12%', positive: true }}
        />
        <StatsCard
          label="Total Users"
          value={totalUsers}
          icon="👥"
          change={{ value: '8%', positive: true }}
        />
        <StatsCard
          label="Total Orders"
          value={totalOrders}
          icon="📦"
          change={{ value: '15%', positive: true }}
        />
        <StatsCard
          label="Avg Order Value"
          value={`$${(totalRevenue / totalOrders).toFixed(2)}`}
          icon="💵"
          change={{ value: '3%', positive: false }}
        />
      </StatsGrid>

      <Chart title="Revenue Trend" type="line" height={200} />
      <Chart title="Orders by Bot" type="bar" height={200} />

      <SectionTitle>Bot Performance</SectionTitle>
      <BotPerformance>
        {bots.map((bot) => (
          <BotCard key={bot.id}>
            <BotHeader>
              <BotName>{bot.name}</BotName>
              <span style={{ color: bot.growth > 0 ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                {bot.growth > 0 ? '↑' : '↓'} {Math.abs(bot.growth)}%
              </span>
            </BotHeader>
            <BotMetrics>
              <Metric>
                <MetricValue>{bot.users}</MetricValue>
                <MetricLabel>Users</MetricLabel>
              </Metric>
              <Metric>
                <MetricValue>{bot.orders}</MetricValue>
                <MetricLabel>Orders</MetricLabel>
              </Metric>
              <Metric>
                <MetricValue>${bot.revenue.toFixed(0)}</MetricValue>
                <MetricLabel>Revenue</MetricLabel>
              </Metric>
            </BotMetrics>
          </BotCard>
        ))}
      </BotPerformance>
    </Layout>
  );
};

export default MasterAnalytics;
